import { Box, Button, Divider, ListItemButton, ListItemIcon, ListItemText, Paper, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import AddIcon from '@mui/icons-material/Add';

import { useDispatch, useSelector } from 'react-redux';
import { loadTopSchemas } from '../../sagas/dataSaga/dataSagaActions';
import { getCountOfSchemas, getIsLoadingTopSchemas, getTopSchemas } from '../../reducers/data/dataReducer';
import { openModelModalAction } from '../../sagas/modelSaga/modelSagaActions';
import { BaseLeftPanelItemSx, Palette, Sizes } from '../../@shared/palette';
import SearchInput from './SearchInput';
import { AppBarHeight } from '../AppBar';
import LoadingShimmer from './LoadingShimmer';
import { useLeftPanel } from '../../hooks/useLeftPanel';
import GuideCallout from '../GuideCallout';
import { TooltipSlotProps } from '../../@shared/templates';
import { getGuideStep, setGuideStep } from '../../reducers/main/mainReducer';
import { GuideStepEnum } from '../../reducers/main/mainModel';
import { useNavigate, useParams } from 'react-router-dom';


const LeftPanel = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoadingTopSchemas = useSelector(getIsLoadingTopSchemas)
  const topSchemas = useSelector(getTopSchemas)
  const allSchemasCount = useSelector(getCountOfSchemas)
  const leftPanelHook = useLeftPanel();
  const guideStep = useSelector(getGuideStep);
  const params = useParams();
  const schema = React.useMemo(() => params.schemaId, [params.schemaId]);

  const handleAddSchema = () => {
    if (guideStep === GuideStepEnum.ClickSchemaBtn) {
      dispatch(setGuideStep(GuideStepEnum.FillSchemaForm));
    }
    dispatch(openModelModalAction())
  }

  const loadMoreSchemasValue = React.useMemo(() => allSchemasCount - (topSchemas?.length ?? 0), [allSchemasCount, topSchemas?.length]);

  const handleLoadMoreSchemas = React.useCallback(() => {
    dispatch(loadTopSchemas())
  }, [dispatch]);

  React.useEffect(() => {
    leftPanelHook.onLoadSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const textOverflow = React.useMemo(() => ({
    overflow: "hidden",
    textOverflow: "unset",
    whiteSpace: "nowrap"
  }), []);

  const schemaItems = React.useMemo(() => {
    const schemas = topSchemas?.map((x, index) => {

      const sx = x.description ? BaseLeftPanelItemSx : {
        paddingTop: "10px",
        paddingBottom: "10px",
        ...BaseLeftPanelItemSx
      };

      return <ListItemButton key={x.id} selected={schema === x.name} sx={sx} onClick={() => {
        navigate(`/${x.name}`);
        if(guideStep === GuideStepEnum.OpenCreatedSchema && index === 0) {
          dispatch(setGuideStep(GuideStepEnum.OpenDatasetTab));
        }
      }}>
        <Tooltip placement="left-start" open={guideStep === GuideStepEnum.OpenCreatedSchema && index === 0} slotProps={TooltipSlotProps} title={
          <GuideCallout>
            Click to open created schema.
          </GuideCallout>
        } arrow>
          <ListItemText sx={{
            position: "relative"
          }}>
            {x.name.length > 35 ? <Tooltip placement="left-start" title={x.name} arrow enterDelay={100}>
              <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary, ...textOverflow }}>{x.name}</Typography>
            </Tooltip> : <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary, ...textOverflow }}>{x.name}</Typography>}
            <Typography sx={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary, ...textOverflow }}>{x.description}</Typography>
            <Box className={"gradient-box"} sx={{
              position: "absolute",
              right: "0px",
              top: "0px",
              width: "30px",
              height: "100%",
              background: `linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)`,
              ":hover": {
                background: "linear-gradient(90deg, rgba(245, 245, 245, 0) 0%, rgba(245, 245, 245, 1) 100%)",
              }
            }}></Box>
          </ListItemText>
        </Tooltip>
        
      </ListItemButton >
    })

    return <>
      {schemas}
      {isLoadingTopSchemas && <LoadingShimmer />}
    </>;
  }, [dispatch, guideStep, isLoadingTopSchemas, navigate, schema, textOverflow, topSchemas]);

  return <Paper
    sx={{
      background: Palette.backgroundColorCard,
      borderRadius: 0,
      width: "300px",
      minWidth: "300px",
      minHeight: `calc(100vh - ${AppBarHeight}px)`,
      maxHeight: `calc(100vh - ${AppBarHeight}px)`,
      overflowY: "hidden",
      overflowX: "hidden",
      marginLeft: "0px"
    }} >
    <Box sx={{ overflow: "hidden", height: "100%", paddingTop: "10px" }}>
      <Box sx={{ paddingRight: "10px" }}>
        <Tooltip placement="left-start" open={guideStep === GuideStepEnum.ClickSchemaBtn} slotProps={TooltipSlotProps} title={
          <GuideCallout>
            Click to open schema creation form and create new schema.
          </GuideCallout>
        } arrow>
          <ListItemButton sx={{ ...BaseLeftPanelItemSx, paddingTop: "10px", paddingBottom: "10px", }} onClick={handleAddSchema}>
            <ListItemIcon>
              <AddIcon sx={{ color: Palette.textColorPrimary }} fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary }}>New schema</Typography>
            </ListItemText>
          </ListItemButton>
        </Tooltip>
        
        <SearchInput leftPanelHook={leftPanelHook} />
        <Divider sx={{ marginBottom: "10px" }} />
      </Box>

      <Box sx={{ overflowY: "scroll", height: "calc(100% - 110px)", paddingRight: "5px" }}>
        {schemaItems}
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          padding: "10px"
        }}>
          {!!loadMoreSchemasValue && <Button sx={{ textTransform: "none", fontSize: Sizes.textPrimary }} variant="text" onClick={handleLoadMoreSchemas}>Load more ({loadMoreSchemasValue})</Button>}
        </Box>
      </Box>
    </Box>
  </Paper>
}


export default LeftPanel;

